import logger from 'loglevel';

import { AbstractStore } from 'models/AbstractStore';
import { IPermanentStore, PermanentStore } from 'models/PermanentStore';
import { RootStore } from 'models/RootStore';

/**
 * There are currently 3 Indexed Databases used:
 * 1. RK.IndexedDB.Permanent
 *      - Created by PermanentStore and stored as the _idbStore field
 *      - PermanentStore is extended by multiple classes that are PermanentStores of a specific model (eg UserPermanentStore)
 *      - This db is not a complete 1-to-1 with psql but it contains many similiar tables
 * 2. RK.IndexedDB.Assets
 *      - AssetBundleIDB extends IndexedDBWrapper
 *      - Stores asset bundles as blobs and some associated data
 * 3. RK.IndexedDB.WebGL
 *      - WebglIDB extends IndexedDBWrapper
 *      - Stores a webgl build. See `Deploying a new webgl build` in the readme
 *
 * PermanentStore can fallback to Local Storage, but AssetBundleIDB and WebglIDB cannot due to them storing large blobs
 * Yes it is a little confusing that this class is used as both an ansestor and a field
 */
export class IndexedDBWrapper extends AbstractStore implements IPermanentStore {
    private static readonly VERSION: number = 3;

    private _db: IDBDatabase;
    private readonly _json: boolean;
    private readonly _tableNames: string[];

    public constructor(rootStore: RootStore, databaseName: string, tableNames: string[], json: boolean = false) {
        super(rootStore, `IndexedDB.${databaseName}`);
        this._json = json;
        this._tableNames = tableNames;
        logger.debug(`RKDB.init: ${databaseName}`);
    }

    private static get IndexedDB(): IDBFactory {
        return window['indexedDB'] || window['mozIndexedDB'] || window['webkitIndexedDB'] || window['msIndexedDB'];
    }

    private static get IndexedDBTransaction(): IDBTransaction {
        return window['IDBTransaction'] || window['webkitIDBTransaction'] || window['msIDBTransaction'];
    }

    private static get IndexedDBKeyRange(): IDBKeyRange {
        return window['IDBKeyRange'] || window['webkitIDBKeyRange'] || window['msIDBKeyRange'];
    }

    private static expiryKey(key: string): string {
        return `${key}_expiry`;
    }

    public open(): Promise<void> {
        let indexedDB: IDBFactory = IndexedDBWrapper.IndexedDB;

        if (!indexedDB) {
            logger.warn(`IndexedDBWrapper<${this.DatabaseName}>.open(): Your browser doesn't support a stable version of IndexedDB.`);
            return Promise.reject();
        }

        return new Promise((resolve, reject) => {
            let dbOpenDBRequest: IDBOpenDBRequest = indexedDB.open(this.DatabaseName, IndexedDBWrapper.VERSION);

            let _reject = reject;
            setTimeout(() => {
                if (dbOpenDBRequest.readyState === 'pending') {
                    logger.error(`IndexedDBWrapper<${this.DatabaseName}>.open(): Timeout - Failed to load IndexedDB.`);
                    _reject();
                }
            }, 1200);

            dbOpenDBRequest.onblocked = () => {
                logger.error(`IndexedDBWrapper<${this.DatabaseName}>.open(): OnBlocked - Failed to load IndexedDB.`);
                reject();
            };

            dbOpenDBRequest.onupgradeneeded = evt => {
                logger.debug(`IndexedDBWrapper<${this.DatabaseName}>.open(): OnUpgradeNeeded ${evt.oldVersion} -> ${evt.newVersion}}`);
                this._db = dbOpenDBRequest.result;
                // Create the store only if it is missing... somehow.
                // TODO: We should check with IndexDB standards as the web evolves.
                this._tableNames.forEach(name => {
                    if (!this._db.objectStoreNames.contains(name)) {
                        // Then create a new store.
                        this._db.createObjectStore(name);
                    }
                });
            };

            dbOpenDBRequest.onsuccess = () => {
                this._db = dbOpenDBRequest.result;
                resolve();
            };

            dbOpenDBRequest.onerror = e => {
                logger.error(`IndexedDBWrapper<${this.DatabaseName}>.open(): OnError - Failed to load IndexedDB. ${JSON.stringify(e)}`);
                reject(e);
            };
        });
    }

    /**
     * Create a IDBTransaction to put a value and an expiry in the db.
     * @param table string. The name of the table in this db. See AssetBundleIDB, WebglIDB, & PermanentStore for possible values.
     * @param key string. The IDB key.
     * @param value T. The value to be stored. JSON.stringify is called on this if `json: true` is passed into the constructor (currently only PermanentStore).
     * @param expiresInMinutes optional number. Every `key` has a corrosponding `key_expiry` column created with an expiry date.
     * @returns Promise. Resolves if transaction completes. Rejects if transaction errors.
     */
    public doStore<T>(table: string, key: string, value: T, expiresInMinutes?: number): Promise<void> {
        return new Promise((resolve, reject) => {
            let transaction: IDBTransaction = this._db.transaction(table, 'readwrite');

            transaction.oncomplete = () => {
                resolve();
            };

            transaction.onerror = () => {
                logger.error(`IndexedDBWrapper<${this.DatabaseName}>.store(${key}): OnError`);
                reject();
            };

            transaction.objectStore(table).put(this._json ? JSON.stringify(value) : value, key);
            transaction.objectStore(table).put(PermanentStore.getExpiresAt(expiresInMinutes), IndexedDBWrapper.expiryKey(key));
        });
    }

    /**
     * Create a IDBTransaction to get a value from the db.
     * @param table string. The name of the table in this db. See AssetBundleIDB, WebglIDB, & PermanentStore for possible values.
     * @param key string. The IDB key.
     * @returns Promise<T | null>. The value from the table. null if it has expired. Rejects if transaction errors.
     */
    public doRead<T>(table: string, key: string): Promise<T | null> {
        return new Promise((resolve, reject) => {
            let transaction: IDBTransaction = this._db.transaction(table, 'readwrite');
            let dataRequest: IDBRequest;
            let expiryRequest: IDBRequest;

            transaction.oncomplete = () => {
                if (PermanentStore.hasExpired(expiryRequest.result)) {
                    resolve(null);
                } else {
                    resolve(dataRequest.result);
                }
            };

            transaction.onerror = () => {
                logger.error(`IndexedDBWrapper<${this.DatabaseName}>.read(${key}): OnError`);
                reject(transaction.error);
            };

            dataRequest = transaction.objectStore(table).get(key);
            expiryRequest = transaction.objectStore(table).get(IndexedDBWrapper.expiryKey(key));
        }).then((result: T | string) => (this._json && typeof result === 'string' ? JSON.parse(result) : result));
    }

    /**
     * Clear the data from a table or all the data from this database.
     * @param table optional string. The table to clear. If null clear this database instead.
     * @returns Promise. Resolves if transaction completes. Rejects if transaction errors.
     */
    public clear(table: string = null): Promise<void> {
        let indexedDB: IDBFactory = IndexedDBWrapper.IndexedDB;

        if (!indexedDB) {
            logger.warn(`IndexedDBWrapper<${this.DatabaseName}>.clear(): Your browser doesn't support a stable version of IndexedDB.`);
            return;
        }

        if (table === null) {
            logger.warn(`IndexedDBWrapper<${this.DatabaseName}>.clear(): Deleting database.`);
            indexedDB.deleteDatabase(this._db.name);
            return;
        }

        return new Promise<void>((resolve, reject) => {
            let transaction: IDBTransaction = this._db.transaction(table, 'readwrite');
            let request: IDBRequest;

            transaction.oncomplete = () => {
                resolve();
            };

            transaction.onerror = () => {
                logger.error(`IndexedDBWrapper<${this.DatabaseName}>.clear(): OnError`);
                reject(transaction.error);
            };

            request = transaction.objectStore(table).clear();
        });
    }
}
